import {
  Entitlements,
  GoalOverviewRoadmapViewOptions,
  ID,
  initialWeekStartsOn,
  InsightsTimeframes,
  insightsTimeframes,
  Interval,
  LimitKeys,
  OrderDirection,
  PmfSurvey,
  SavedUserSubscription,
  SortingMode,
  SubscriptionType,
  TodayGroupBy,
  UsageStatisticsEntry,
  User,
  UserLimits,
  UserSettings,
} from '@griply/types';
import { getLimits, getPremiumLimits } from '@griply/utils';
import {
  addYears,
  differenceInDays,
  endOfDay,
  getYear,
  startOfDay,
} from 'date-fns';

import { DocumentSnapshot } from '../types/firestore';
import { coerceCustomTaskOrder, coerceIdArrayMap } from './custom-task-order';
import {
  coerceBooleanFlag,
  coerceFilterDocument,
  coerceGoalFilterDocument,
  coerceSortingMode,
} from './filter-document';
import {
  firebaseObjectTimestampToDate,
  firebaseTimestampToDate,
} from './firebase-timestamp-to-date';
import { isPlainRecord } from './is-plain-record';
import { isValidDate } from './is-valid-date';
import { reconcileCustomTaskOrder } from './reconcile-custom-task-order';

const todayGroupByOptions = Object.values(TodayGroupBy);

// the reorder migration persists literal nulls into legacy buckets — drop on read
const coerceTodayGroupSorting = (
  value: unknown,
): UserSettings['todayGroupSorting'] => {
  if (!isPlainRecord(value)) {
    return undefined;
  }
  return Object.entries(value).reduce<
    Record<string, { sorting: Record<ID, ID[]> }>
  >((acc, [groupKey, rawGroup]) => {
    if (!isPlainRecord(rawGroup)) {
      return acc;
    }
    const sorting = coerceIdArrayMap(rawGroup.sorting);
    if (sorting === undefined) {
      return acc;
    }
    acc[groupKey] = { sorting };
    return acc;
  }, {}) as UserSettings['todayGroupSorting'];
};

type RawLimitEntry = {
  enforcedAt?: unknown;
  limit?: number;
};

const processLimits = (
  limits: Record<string, RawLimitEntry> | null | undefined,
  isPremium: boolean,
): UserLimits => {
  const limitBase = isPremium ? getPremiumLimits() : getLimits();
  return Object.keys(limitBase).reduce<UserLimits>((acc, limitKey) => {
    const key = limitKey as LimitKeys;
    const userLimit = limits?.[key];
    const limitBackup = limitBase[key];

    acc[key] = {
      limit: userLimit?.limit ?? limitBackup.limit,
      enforcedAt: userLimit?.enforcedAt
        ? firebaseTimestampToDate(userLimit.enforcedAt as Date) ?? new Date()
        : new Date(),
    };

    return acc;
  }, {} as UserLimits);
};

const coercePmfSurvey = (value: unknown): PmfSurvey | undefined => {
  if (!isPlainRecord(value)) {
    return undefined;
  }
  return {
    surveyShownCount:
      typeof value.surveyShownCount === 'number' && value.surveyShownCount >= 0
        ? value.surveyShownCount
        : 0,
    lastTimeShown: isValidDate(value.lastTimeShown)
      ? value.lastTimeShown
      : null,
    surveyCompleted: value.surveyCompleted === true,
  };
};

const coerceUsageStatistics = (value: unknown): UsageStatisticsEntry[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (entry): entry is UsageStatisticsEntry =>
      isPlainRecord(entry) &&
      isValidDate(entry.date) &&
      typeof entry.completionCount === 'number' &&
      entry.completionCount >= 0,
  );
};

const coerceStringTimestamp = (
  value: string | Date | null | undefined,
): Date | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// An unmapped/future RevenueCat entitlement keeps premium access rather than dropping it.
const coerceSubscriptionType = (value: unknown): SubscriptionType =>
  Object.values(SubscriptionType).includes(value as SubscriptionType)
    ? (value as SubscriptionType)
    : SubscriptionType.Premium;

const processSubscription = (subscription: SavedUserSubscription) => {
  const purchasedAt = coerceStringTimestamp(subscription.purchasedAt);
  const expiresAt = coerceStringTimestamp(subscription.expiresAt);
  const unsubscribedAt = coerceStringTimestamp(subscription.unsubscribedAt);
  const refundedAt = coerceStringTimestamp(subscription.refundedAt);

  const subscriptionDuration =
    expiresAt && purchasedAt
      ? differenceInDays(endOfDay(expiresAt), startOfDay(purchasedAt))
      : Infinity;

  const interval = !expiresAt
    ? Interval.Lifetime
    : subscriptionDuration <= 7
      ? Interval.Week
      : subscriptionDuration <= 14
        ? Interval.TwoWeeks
        : subscriptionDuration <= 31
          ? Interval.Month
          : subscriptionDuration > 3_650
            ? Interval.Lifetime
            : Interval.Year;

  return {
    type: coerceSubscriptionType(subscription.type),
    store: subscription.store,
    stripeCustomerId: subscription.stripeCustomerId ?? undefined,
    managementUrl: subscription.managementUrl,
    productId: subscription.productId,
    purchasedAt: purchasedAt ?? new Date(0),
    expiresAt: interval === Interval.Lifetime ? null : expiresAt,
    unsubscribedAt: unsubscribedAt ?? undefined,
    refundedAt: refundedAt ?? undefined,
    interval: subscription.productId?.includes('custom')
      ? Interval.Lifetime
      : interval,
  };
};

const coerceReminderTime = (value: unknown): number | null | undefined =>
  value === null ? null : typeof value === 'number' ? value : undefined;

const coerceReminderSetting = (
  value: unknown,
): { time?: number | null } | undefined =>
  isPlainRecord(value)
    ? { ...value, time: coerceReminderTime(value.time) }
    : undefined;

// plannedTask/allDayTask.time: unknown coerces to absent = the default offset applies; only null is off.
const coerceNotifications = (value: unknown) => {
  if (!isPlainRecord(value)) {
    return undefined;
  }
  return {
    ...value,
    ...(value.plannedTask !== undefined
      ? { plannedTask: coerceReminderSetting(value.plannedTask) }
      : {}),
    ...(value.allDayTask !== undefined
      ? { allDayTask: coerceReminderSetting(value.allDayTask) }
      : {}),
  };
};

export const userConverter = {
  fromFirestore: (snapshot: DocumentSnapshot): User => {
    const data = firebaseObjectTimestampToDate(snapshot.data()) as Record<
      string,
      unknown
    >;

    const settings = (data.settings ?? {}) as Record<string, unknown>;
    const rawFilters = settings.filters;
    const filtersIsObject =
      rawFilters !== null &&
      rawFilters !== undefined &&
      typeof rawFilters === 'object' &&
      !Array.isArray(rawFilters);
    const filtersRecord = filtersIsObject
      ? (rawFilters as Record<string, unknown>)
      : null;
    const coercedFilters =
      rawFilters === undefined
        ? undefined
        : filtersRecord === null
          ? null
          : {
              today: coerceFilterDocument(filtersRecord.today),
              upcoming: coerceFilterDocument(filtersRecord.upcoming),
              allTasks: coerceFilterDocument(filtersRecord.allTasks),
              inbox: coerceFilterDocument(filtersRecord.inbox),
              goalsOverview: coerceGoalFilterDocument(
                filtersRecord.goalsOverview,
              ),
              goalsTimeline: coerceGoalFilterDocument(
                filtersRecord.goalsTimeline,
              ),
              projectsOverview: coerceGoalFilterDocument(
                filtersRecord.projectsOverview,
              ),
            };

    const coercedCustomTaskOrder = reconcileCustomTaskOrder(
      coerceCustomTaskOrder(settings.customTaskOrder),
      settings,
      new Date(),
    );

    const goalTabSorting = (settings.goalTabSorting ?? {}) as Record<
      string,
      { sorting?: unknown }
    >;
    const insightsSettings = (settings.insights ?? {}) as Record<
      string,
      unknown
    >;
    const insightsTimeframe = insightsSettings.timeframe as
      | InsightsTimeframes
      | null
      | undefined;
    const todayGroupByValue = settings.todayGroupBy as TodayGroupBy | undefined;

    const goalsOverviewSettings = (settings.goalsOverview ?? {}) as Record<
      string,
      unknown
    >;
    const goalOverviewViewOptions = Object.values(
      GoalOverviewRoadmapViewOptions,
    );
    const coerceGoalOverviewView = (value: unknown) =>
      goalOverviewViewOptions.includes(value as GoalOverviewRoadmapViewOptions)
        ? (value as GoalOverviewRoadmapViewOptions)
        : undefined;

    const coerceOrderDirection = (
      value: unknown,
    ): OrderDirection | undefined =>
      Object.values(OrderDirection).includes(value as OrderDirection)
        ? (value as OrderDirection)
        : undefined;

    const coerceBooleanFlagRecord = (
      value: unknown,
      flagKeys: string[],
    ): Record<string, unknown> | undefined => {
      if (!isPlainRecord(value)) {
        return undefined;
      }
      const coerced = { ...value };
      flagKeys.forEach((key) => {
        if (key in coerced) {
          coerced[key] = coerceBooleanFlag(coerced[key]);
        }
      });
      return coerced;
    };

    const goalsOverviewTimeline = coerceBooleanFlagRecord(
      goalsOverviewSettings.timeline,
      ['showCompletedGoals'],
    );
    const projectsOverviewSettings = isPlainRecord(settings.projectsOverview)
      ? {
          ...settings.projectsOverview,
          roadmap: coerceBooleanFlagRecord(settings.projectsOverview.roadmap, [
            'showCompletedProjects',
          ]),
        }
      : undefined;

    const rawUpcoming = settings.upcoming;
    const coercedUpcoming = isPlainRecord(rawUpcoming)
      ? {
          ...rawUpcoming,
          ...(rawUpcoming.sortingMode !== undefined
            ? {
                sortingMode: coerceSortingMode(rawUpcoming.sortingMode) ?? null,
              }
            : {}),
        }
      : undefined;

    const entitlements = (data.entitlements ?? []) as Entitlements[];
    const isPremium = entitlements.includes(Entitlements.Premium);

    // forward-compat: not applicable — nextTaskSummaryAt is backend-written bookkeeping, carried through by
    // the spread as a Date/null and read by nothing on either client.
    return {
      ...data,
      name: data.name ?? '',
      settings: {
        ...settings,
        startOfWeek: settings.startOfWeek ?? initialWeekStartsOn,
        goalTabSorting: {
          ...goalTabSorting,
          all: {
            sorting: settings.goalSorting,
            ...(goalTabSorting.all ?? {}),
          },
        },
        goalRoadmapSorting: settings.goalRoadmapSorting ?? {
          inProgress: goalTabSorting.active?.sorting ?? [],
          [getYear(new Date())]:
            goalTabSorting[getYear(new Date())]?.sorting ?? [],
          [getYear(addYears(new Date(), 1))]:
            goalTabSorting[getYear(new Date())]?.sorting ?? [],
        },
        todayGroupBy: todayGroupByOptions.includes(todayGroupByValue!)
          ? todayGroupByValue
          : TodayGroupBy.None,
        todoSortingMode: settings.todoSortingMode ?? SortingMode.DueDate,
        todoSorting: coerceIdArrayMap(settings.todoSorting),
        todayGroupSorting: coerceTodayGroupSorting(settings.todayGroupSorting),
        insights: {
          ...insightsSettings,
          timeframe: insightsTimeframe
            ? insightsTimeframes.includes(insightsTimeframe)
              ? insightsTimeframe
              : InsightsTimeframes.Year
            : null,
        },
        goalsOverview: {
          ...goalsOverviewSettings,
          mobileView: coerceGoalOverviewView(goalsOverviewSettings.mobileView),
          ...(goalsOverviewTimeline !== undefined
            ? { timeline: goalsOverviewTimeline }
            : {}),
        },
        ...(projectsOverviewSettings !== undefined
          ? { projectsOverview: projectsOverviewSettings }
          : {}),
        ...('goalSortDirection' in settings
          ? {
              goalSortDirection: coerceOrderDirection(
                settings.goalSortDirection,
              ),
            }
          : {}),
        ...('projectSortDirection' in settings
          ? {
              projectSortDirection: coerceOrderDirection(
                settings.projectSortDirection,
              ),
            }
          : {}),
        upcoming: coercedUpcoming,
        ...(settings.notifications !== undefined
          ? { notifications: coerceNotifications(settings.notifications) }
          : {}),
        ...(coercedFilters !== undefined ? { filters: coercedFilters } : {}),
        ...(coercedCustomTaskOrder !== undefined
          ? { customTaskOrder: coercedCustomTaskOrder }
          : {}),
      },
      limits: processLimits(
        data.limits as Record<string, RawLimitEntry> | null | undefined,
        isPremium,
      ),
      subscriptions: Array.isArray(data.subscriptions)
        ? (data.subscriptions as SavedUserSubscription[]).map(
            processSubscription,
          )
        : null,
      pmfSurvey: coercePmfSurvey(data.pmfSurvey),
      usageStatistics: coerceUsageStatistics(data.usageStatistics),
      externalCalendarEventsChangedAt:
        data.externalCalendarEventsChangedAt instanceof Date
          ? data.externalCalendarEventsChangedAt
          : undefined,
      // forward-compat: not applicable — derived from the Auth session on read, never stored; a read path that knows it sets it.
      signInProvider: null,
    } as unknown as User;
  },
  toFirestore: (user: User) => user,
};
